import React, { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ShieldCheck, ShieldX, Eye, Crown } from "lucide-react";

const ALL_PERMS = ["view", "create", "edit", "delete", "admin"];
export const PERM_LABELS = { view: "Voir", create: "Créer", edit: "Modifier", delete: "Supprimer", admin: "Administrer" };

export default function PermissionMatrix({ modules, value, onChange }) {
  const [search, setSearch] = useState("");

  const cell = (mkey) => value[mkey] || {};

  const toggle = (mkey, perm, checked, applicable) => {
    const next = { ...(value[mkey] || {}) };
    if (perm === "admin") {
      if (checked) applicable.forEach((p) => (next[p] = true));
      else next.admin = false;
    } else {
      next[perm] = checked;
      if (!checked) next.admin = false;
      // if all sub-perms enabled and admin applicable, keep admin as is (do not auto-enable)
    }
    onChange({ ...value, [mkey]: next });
  };

  const applyAll = (on, includeAdmin = true) => {
    const next = {};
    modules.forEach((m) => {
      next[m.key] = {};
      m.perms.forEach((p) => {
        if (p === "admin" && !includeAdmin) return;
        next[m.key][p] = on;
      });
    });
    onChange(next);
  };

  const applyReadOnly = () => {
    const next = {};
    modules.forEach((m) => {
      next[m.key] = {};
      if (m.perms.includes("view")) next[m.key].view = true;
    });
    onChange(next);
  };

  const filtered = modules.filter((m) => m.label.toLowerCase().includes(search.toLowerCase()));
  const groups = filtered.reduce((acc, m) => {
    const g = m.group || "Autres";
    (acc[g] = acc[g] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-3 max-w-full overflow-x-auto">
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" className="h-9 border-2 border-green-300 text-green-800" onClick={() => applyAll(true, false)} data-testid="perm-allow-all">
          <ShieldCheck className="h-4 w-4 mr-1" /> Tout autoriser
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-9 border-2 border-slate-300 text-slate-700" onClick={() => applyAll(false)} data-testid="perm-deny-all">
          <ShieldX className="h-4 w-4 mr-1" /> Tout désactiver
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-9 border-2 border-blue-300 text-blue-800" onClick={applyReadOnly} data-testid="perm-readonly">
          <Eye className="h-4 w-4 mr-1" /> Lecture seule
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-9 border-2 border-amber-300 text-amber-800" onClick={() => applyAll(true, true)} data-testid="perm-full-admin">
          <Crown className="h-4 w-4 mr-1" /> Administrateur complet
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un module…"
          className="h-10 pl-9" data-testid="perm-search" />
      </div>

      <div className="overflow-x-auto rounded-xl border-2 border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="text-left px-3 py-2 font-display">Module</th>
              {ALL_PERMS.map((p) => (
                <th key={p} className="px-2 py-2 font-display text-center w-16">{PERM_LABELS[p]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(groups).map(([groupName, mods]) => (
              <React.Fragment key={groupName}>
                <tr className="bg-slate-100">
                  <td colSpan={ALL_PERMS.length + 1} className="px-3 py-1.5 font-display font-extrabold text-xs uppercase tracking-wide text-slate-500">
                    {groupName}
                  </td>
                </tr>
                {mods.map((m, i) => {
                  const c = cell(m.key);
                  return (
                    <tr key={m.key} className={i % 2 ? "bg-slate-50/60" : ""} data-testid={`perm-row-${m.key}`}>
                      <td className="px-3 py-2 font-semibold text-slate-800">{m.label}</td>
                      {ALL_PERMS.map((p) => {
                        const applicable = m.perms.includes(p);
                        return (
                          <td key={p} className="px-2 py-2 text-center">
                            {applicable ? (
                              <Checkbox
                                checked={!!c[p]}
                                onCheckedChange={(v) => toggle(m.key, p, !!v, m.perms)}
                                data-testid={`perm-${m.key}-${p}`}
                                className="mx-auto data-[state=checked]:bg-blue-700 data-[state=checked]:border-blue-700 h-5 w-5"
                              />
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
